// Deterministic data and pure helpers behind the S1-17 routes of the in-memory API (fake.ts):
// metric definitions (the DE catalog, and the gate catalog on HTVA's company), the seeded gate
// observations (12 pitches and 1 deposit by 2026-11-10, S1-17 acceptance 1), people, seats and
// subscriptions for Setup, the Friday report HTML, and CSV import validation. Shapes are the
// SDK's; `fake-contract.test.ts` parses every response with its zod contract.
import type { GateDef } from "@companyos/domain-kpi";
import { weekStart } from "../focus/weeks";
import { CATALOG, COMPANY_IDS, type FakeCompany, type FakeDe, SHARED_DE, uid } from "./fake-data";
import { FAKE_GATE_DEFS, FAKE_OPERATOR_COMPANY_ID } from "./fake-gate-data";
import { sharedDesSeats } from "./fake-shared-des";
import type {
  DeSubscription,
  FridayReportRecord,
  ImportRowResult,
  MetricDef,
  Observation,
  Person,
  Seat,
  TargetRule,
} from "./types";

const EPOCH = "2026-06-01T09:00:00Z";

// Metric definitions ----------------------------------------------------------------------------

const GATE_CADENCE: Record<string, MetricDef["cadence"]> = {
  "gate.close_rate": "weekly",
  "gate.install_cash_multiple": "monthly",
  "gate.supervisor_hours_per_de": "weekly",
  "gate.founder_hours_per_de": "weekly",
  "gate.htva_recurring_revenue": "monthly",
  "gate.cash_floor": "weekly",
};

const GATE_FORMULA: Record<string, string> = {
  "gate.close_rate": "gate.deposits / gate.pitches",
  "gate.supervisor_hours_per_de": "fan-out: mean of de.supervisor_hours over client DE seats",
  "gate.founder_hours_per_de": "fan-out: mean of de.founder_hours over client DE seats",
};

const companyNo = (companyId: string) => Number.parseInt(companyId.slice(-4), 16) % 0x0fff;

/** The id of a gate metric on the demo operator company. */
export function gateMetricId(key: string): string {
  return uid(0x3f00, FAKE_GATE_DEFS.findIndex((g) => g.key === key) + 1);
}

function def(
  companyId: string,
  id: string,
  m: Pick<
    MetricDef,
    "key" | "name" | "unit" | "direction" | "aggregation" | "cadence" | "visibility"
  > &
    Partial<
      Pick<
        MetricDef,
        "source" | "formula" | "event_query" | "description" | "needs_review" | "bands" | "gate"
      >
    >,
): MetricDef {
  const source = m.source ?? "manual";
  return {
    company_id: companyId,
    id,
    key: m.key,
    name: m.name,
    description: m.description ?? null,
    unit: m.unit,
    direction: m.direction,
    aggregation: m.aggregation,
    normalization: null,
    cadence: m.cadence,
    source,
    event_query: source === "event" ? (m.event_query ?? null) : null,
    formula: source === "formula" ? (m.formula ?? null) : null,
    task_query: null,
    visibility: m.visibility,
    gap_policy: "excuse",
    needs_review: m.needs_review ?? false,
    archived_at: null,
    bands: m.bands ?? null,
    gate: m.gate ?? null,
    actor_kind: "system",
    actor_membership_id: null,
    created_at: EPOCH,
    updated_at: EPOCH,
  };
}

function gateDef(companyId: string, g: GateDef): MetricDef {
  const formula = GATE_FORMULA[g.key];
  return def(companyId, gateMetricId(g.key), {
    key: g.key,
    name: g.label,
    description: g.spec.condition,
    unit: g.unit,
    direction: g.direction,
    aggregation: g.spec.measure === "sum" ? "cumulative" : "average",
    cadence: GATE_CADENCE[g.key] ?? "daily",
    visibility: "agency",
    gate: g.spec,
    ...(formula ? { source: "formula" as const, formula } : {}),
  });
}

/**
 * AGENT-REPORTING: Acme's manual weekly metrics with reporting duties (fake-reporting.ts): two on
 * Riley's seat (one flagged for review) and one on the Recruiting lead's seat.
 */
export const REPORTING_SEED_METRICS = [
  { n: 1, key: "de.intros_drafted", name: "Candidate intros drafted", needs_review: false },
  { n: 2, key: "de.offers_accepted", name: "Offers accepted", needs_review: true },
  { n: 3, key: "recruiting.debriefs", name: "Interview debriefs logged", needs_review: false },
] as const;
export type ReportingSeedKey = (typeof REPORTING_SEED_METRICS)[number]["key"];

export function reportingMetricId(key: ReportingSeedKey): string {
  return uid(0x3d90, REPORTING_SEED_METRICS.find((m) => m.key === key)?.n ?? 0);
}

function reportingDefs(company: FakeCompany): MetricDef[] {
  if (company.id !== COMPANY_IDS.acme) return [];
  return REPORTING_SEED_METRICS.map((m) =>
    def(company.id, reportingMetricId(m.key), {
      key: m.key,
      name: m.name,
      unit: "number",
      direction: "gte",
      aggregation: "cumulative",
      cadence: "weekly",
      visibility: "client",
      needs_review: m.needs_review,
    }),
  );
}

/** The company's metric definitions: the gate catalog on HTVA, the DE catalog elsewhere. */
export function metricDefs(company: FakeCompany): MetricDef[] {
  if (company.id === FAKE_OPERATOR_COMPANY_ID)
    return FAKE_GATE_DEFS.map((g) => gateDef(company.id, g));
  return [...catalogDefs(company), ...reportingDefs(company)];
}

function catalogDefs(company: FakeCompany): MetricDef[] {
  return CATALOG.map((m, i) =>
    def(company.id, uid(0x3000 + companyNo(company.id), i + 1), {
      key: m.key,
      name: m.name,
      unit: m.unit,
      direction: m.direction,
      aggregation: m.unit === "percent" ? "average" : "cumulative",
      cadence: m.key === "de.supervisor_hours" || m.key === "de.founder_hours" ? "weekly" : "daily",
      visibility: m.visibility,
      ...(m.key === "de.approval_rate"
        ? {
            source: "formula" as const,
            formula: "output.approved / (output.approved + output.rejected)",
          }
        : {
            source: "event" as const,
            event_query: { event_type: "activity.recorded", measure: "count" as const },
          }),
    }),
  );
}

// Observations ----------------------------------------------------------------------------------

/** Period type of a metric's cadence (the period a manual entry is for). */
export function periodTypeOf(cadence: MetricDef["cadence"]): Observation["period_type"] {
  switch (cadence) {
    case "weekly":
      return "week";
    case "monthly":
      return "month";
    case "quarterly":
      return "quarter";
    case "yearly":
      return "year";
    default:
      return "day";
  }
}

/** First day of a period key (day, ISO week, month). */
export function periodStartsOn(type: Observation["period_type"], key: string): string {
  if (type === "week") return weekStart(key).toISOString().slice(0, 10);
  if (type === "month") return `${key}-01`;
  return key;
}

export function observationRow(
  companyId: string,
  n: number,
  o: Pick<Observation, "metric_def_id" | "period_type" | "period_key" | "note"> & {
    value: number | null;
    supersedes_id?: string | null;
    created_at?: string;
    /** Default: the company; the scorecard grid enters a seat's numbers (HOME-CAD-UX). */
    subject?: { kind: Observation["subject_kind"]; id: string };
  },
): Observation {
  return {
    company_id: companyId,
    id: uid(0x5f00, n),
    metric_def_id: o.metric_def_id,
    subject_kind: o.subject?.kind ?? "company",
    subject_id: o.subject?.id ?? companyId,
    period_type: o.period_type,
    period_key: o.period_key,
    period_starts_on: periodStartsOn(o.period_type, o.period_key),
    value: o.value === null ? null : String(o.value),
    status: "on_time",
    source: "manual",
    source_event_id: `manual:fake:${n}`,
    source_event_ids: null,
    supersedes_id: o.supersedes_id ?? null,
    tombstone: false,
    note: o.note,
    entered_by_membership_id: null,
    actor_kind: "agency_user",
    actor_membership_id: null,
    created_at: o.created_at ?? EPOCH,
    provenance: null,
  };
}

/**
 * HTVA's seeded gate numbers: 12 warm pitches from 2026-10-16 to 2026-11-10 (one "no" day with
 * its reasons), 1 qualifying deposit on 2026-11-10, the October revenue baseline and a cash
 * reading. S1-17 acceptance 1 opens the gate dashboard on `as_of = 2026-11-10`.
 */
export function seedGateObservations(): Observation[] {
  const co = COMPANY_IDS.htva;
  const rows: [string, Observation["period_type"], string, number, string | null][] = [
    ["gate.pitches", "day", "2026-10-20", 3, null],
    ["gate.pitches", "day", "2026-10-27", 4, null],
    ["gate.pitches", "day", "2026-11-03", 3, null],
    ["gate.pitches", "day", "2026-11-10", 2, null],
    ["gate.pitch_declined", "day", "2026-10-27", 2, "price too high; timing (Q1 hiring freeze)"],
    ["gate.deposits", "day", "2026-11-10", 1, "Acme Recruiting, $5,000"],
    ["gate.deposit_amount", "day", "2026-11-10", 500_000, null],
    ["gate.htva_recurring_revenue", "month", "2026-10", 12_400_000, "October baseline"],
    ["gate.cash_floor", "week", "2026-W43", 6_150_000, null],
    ["gate.delivery_posts", "day", "2026-10-23", 1, "First delivery log"],
  ];
  return rows.map(([key, type, period, value, note], i) =>
    observationRow(co, i + 1, {
      metric_def_id: gateMetricId(key),
      period_type: type,
      period_key: period,
      value,
      note,
      created_at: `${periodStartsOn(type, period)}T18:00:00Z`,
    }),
  );
}

// Target rules ----------------------------------------------------------------------------------

/**
 * The company's target rules (subject = company). On HTVA: the verdict's sales, deposit and
 * install numbers and the close-rate band are persisted rules (S1-17 review #9); the other gates
 * have none, so the gate tracker shows their verdict value flagged as a fallback. Elsewhere: one
 * company-level target on the first catalog metric.
 */
export function targetRules(company: FakeCompany): TargetRule[] {
  const rule = (n: number, metric: string, value: number, max: number | null = null) => ({
    company_id: company.id,
    id: uid(0x7a00 + companyNo(company.id), n),
    metric_def_id: metric,
    subject_kind: "company" as const,
    subject_id: company.id,
    period_type: "day" as const,
    value: String(value),
    value_max: max === null ? null : String(max),
    effective_from: "2026-10-16",
    effective_to: null,
    actor_kind: "agency_user" as const,
    actor_membership_id: null,
    created_at: EPOCH,
    updated_at: EPOCH,
  });
  if (company.id === COMPANY_IDS.htva)
    return [
      rule(1, gateMetricId("gate.pitches"), 20),
      rule(2, gateMetricId("gate.deposits"), 2),
      rule(3, gateMetricId("gate.installs"), 3),
      rule(4, gateMetricId("gate.close_rate"), 0.3, 0.8),
    ];
  const first = metricDefs(company)[0];
  return first ? [rule(1, first.id, 40)] : [];
}

// Setup lists -----------------------------------------------------------------------------------

const slugEmail = (name: string, company: FakeCompany) =>
  `${name.toLowerCase().replace(/[^a-z]+/g, ".")}@${company.slug}.example`;

function person(company: FakeCompany, id: string, name: string, roles: Person["roles"]): Person {
  return {
    company_id: company.id,
    id,
    user_id: uid(0x0e00, Number.parseInt(id.slice(-3), 16)),
    kind: "person",
    status: "active",
    display_name: name,
    avatar_key: null,
    owner_membership_id: null,
    joined_at: EPOCH,
    actor_kind: "system",
    actor_membership_id: null,
    created_at: EPOCH,
    updated_at: EPOCH,
    email: slugEmail(name, company),
    roles,
  };
}

/** The founder's membership name in every client company (agency staff). */
export const FOUNDER_NAME = "HTVA founder";

/**
 * The company's people. The founder's membership (HTVA staff, whose founder hours are
 * agency-visibility) is listed for agency operators only.
 */
export function people(company: FakeCompany, des: FakeDe[], operator: boolean): Person[] {
  const out = new Map<string, Person>();
  for (const d of des) {
    // SHARED-DES: the Fulfillment Lead holds a seat but is not on the roster (the seeded
    // people count, which Home's checklist reads, stays as it was); `sharedDesName` names her.
    if (d.membership_id === SHARED_DE.de) continue;
    out.set(
      d.supervisor.membership_id,
      person(company, d.supervisor.membership_id, d.supervisor.name, ["admin"]),
    );
    if (operator)
      out.set(d.founder_membership_id, person(company, d.founder_membership_id, FOUNDER_NAME, []));
  }
  return [...out.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
}

function seat(company: FakeCompany, id: string, title: string, supervisor: string | null): Seat {
  return {
    company_id: company.id,
    id,
    role_id: uid(0x2f00, companyNo(company.id)),
    title,
    supervisor_seat_id: supervisor,
    staff: false,
    kind: "standard",
    status: "filled",
    origin: "template",
    origin_ref: null,
    settings: {},
    de_fill: null,
    version: 1,
    actor_kind: "system",
    actor_membership_id: null,
    created_at: EPOCH,
    updated_at: EPOCH,
  };
}

export function seats(company: FakeCompany, des: FakeDe[]): Seat[] {
  const out = new Map<string, Seat>();
  for (const d of des) {
    out.set(d.supervisor.seat_id, seat(company, d.supervisor.seat_id, d.supervisor.title, null));
    out.set(d.seat_id, seat(company, d.seat_id, d.seat_title, d.supervisor.seat_id));
  }
  // One open DE seat per company (under the first supervisor seat), for the DE create wizard.
  const open = openSeatOf(company, des);
  if (open && !out.has(open.id)) out.set(open.id, open);
  // SHARED-DES: Acme's CEO, Sales and Recruiting seats around Account Health's home.
  return sharedDesSeats(company, [...out.values()], (id, title, supervisor) =>
    seat(company, id, title, supervisor),
  );
}

/** The company's open DE seat: no DE on it yet, supervised by the first DE's supervisor seat. */
export function openSeatOf(company: FakeCompany, des: FakeDe[]): Seat | null {
  const supervisor = des[0]?.supervisor;
  if (!supervisor) return null;
  const id = uid(0x2000, 0xd00 + companyNo(company.id));
  if (des.some((d) => d.seat_id === id)) return null;
  return {
    ...seat(company, id, "Interview scheduler (DE)", supervisor.seat_id),
    status: "open",
  };
}

export function subscriptions(company: FakeCompany, des: FakeDe[]): DeSubscription[] {
  return des.map((d, i) => ({
    company_id: company.id,
    id: uid(0x8f00, companyNo(company.id) * 16 + i),
    seat_id: d.seat_id,
    membership_id: d.membership_id,
    amount_minor: 150_000,
    currency: company.reporting_currency,
    cadence: "monthly",
    start_month: "2026-06",
    end_month: null,
    scope: "client",
    maintenance_plan_id: null,
    tier: null,
    started_on: null,
    ended_on: null,
    actor_kind: "system",
    actor_membership_id: null,
    created_at: EPOCH,
    updated_at: EPOCH,
  }));
}

// Friday report HTML ----------------------------------------------------------------------------

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/**
 * The stored HTML of a report version (the email body; the real one is rendered by
 * services/api/src/reports/friday). Inline CSS, no scripts, no external assets.
 */
export function reportHtml(
  company: FakeCompany,
  report: FridayReportRecord,
  des: FakeDe[],
): string {
  const names = des.map((d) => escapeHtml(d.display_name)).join(", ") || "No digital employees";
  const note = report.supervisor_note
    ? `<section><h2>Supervisor note</h2><p>${escapeHtml(report.supervisor_note)}</p></section>`
    : "";
  return [
    "<!doctype html>",
    '<html lang="en"><head><meta charset="utf-8">',
    `<title>Friday report · ${escapeHtml(company.name)} · ${report.week_key}</title>`,
    "<style>body{font-family:system-ui,sans-serif;margin:24px;line-height:1.5}h1{font-size:20px}h2{font-size:16px;margin-top:20px}footer{margin-top:32px;font-size:12px}</style>",
    "</head><body>",
    `<h1>${escapeHtml(company.name)} · week ${report.week_key}</h1>`,
    `<p>Version ${report.version}. Digital employees: ${names}.</p>`,
    "<section><h2>Summary</h2><p>Work done, outcomes, approval rate, cost and supervisor hours for the week.</p></section>",
    note,
    `<footer>Generated by CompanyOS for ${escapeHtml(company.name)} · questions → operator@htva.example</footer>`,
    "</body></html>",
  ].join("");
}

// CSV imports -----------------------------------------------------------------------------------

/** RFC 4180-ish: quoted fields, doubled quotes, CRLF or LF. Blank lines are skipped. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  const src = text.replace(/^﻿/, "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(field);
      if (row.some((f) => f.trim() !== "")) rows.push(row);
      row = [];
      field = "";
    } else field += ch;
  }
  row.push(field);
  if (row.some((f) => f.trim() !== "")) rows.push(row);
  return rows;
}

export type ImportContext = {
  seats: { id: string; title: string }[];
  people: { id: string; email: string | null }[];
  operator: boolean;
};

type RowError = NonNullable<ImportRowResult["errors"]>[number];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_KINDS = ["work", "supervision", "founder", "setup", "other"];

/** One validated import row: its result and, when ok, the values a commit writes. */
export type ValidatedRow = {
  result: ImportRowResult;
  time?: {
    seat_id: string;
    membership_id: string;
    kind: string;
    work_date: string;
    seconds: number;
    note: string | null;
  };
};

/** Per-row validation of a `time-entries` or `costs` CSV (13 §Imports columns). */
export function validateImport(
  resource: "time-entries" | "costs",
  text: string,
  ctx: ImportContext,
): { header: string[]; rows: ValidatedRow[] } | { error: string } {
  const [header, ...data] = parseCsv(text);
  if (!header) return { error: "the file is empty" };
  const cols = header.map((h) => h.trim().toLowerCase());
  const required =
    resource === "time-entries"
      ? ["work_date", "grain", "seat", "person", "kind", "minutes"]
      : ["occurred_at", "seat", "kind", "amount", "currency", "meter_identity", "visibility"];
  const missing = required.filter((c) => !cols.includes(c));
  if (missing.length) return { error: `missing columns: ${missing.join(", ")}` };
  if (data.length === 0) return { error: "the file has no data rows" };
  if (data.length > 5000) return { error: "at most 5000 rows per file" };
  const seen = new Set<string>();
  const rows = data.map((cells, i): ValidatedRow => {
    const get = (c: string) => (cells[cols.indexOf(c)] ?? "").trim();
    const errors: RowError[] = [];
    const err = (column: string, code: string, message: string) =>
      errors.push({ column, code, message });
    const seat = ctx.seats.find((s) => s.id === get("seat") || s.title === get("seat"));
    if (!seat) err("seat", "unknown_seat", `no seat "${get("seat")}"`);
    let time: ValidatedRow["time"];
    if (resource === "time-entries") {
      const date = get("work_date");
      if (!ISO_DATE.test(date) || Number.isNaN(Date.parse(date)))
        err("work_date", "invalid_date", "work_date must be YYYY-MM-DD");
      if (!["day", "week"].includes(get("grain"))) err("grain", "invalid", "grain is day or week");
      const who = ctx.people.find((p) => p.email === get("person") || p.id === get("person"));
      if (!who) err("person", "unknown_person", `no person "${get("person")}"`);
      const kind = get("kind");
      if (!TIME_KINDS.includes(kind))
        err("kind", "invalid", `kind is one of ${TIME_KINDS.join(", ")}`);
      else if (kind === "founder" && !ctx.operator)
        err("kind", "forbidden", "founder time is entered by the agency");
      const minutes = Number(get("minutes"));
      if (!Number.isInteger(minutes) || minutes <= 0)
        err("minutes", "invalid", "minutes must be a whole number above 0");
      const key = `${get("seat")}|${get("person")}|${kind}|${date}`;
      if (seen.has(key)) err("work_date", "duplicate_in_file", "the same row appears twice");
      seen.add(key);
      if (!errors.length && seat && who)
        time = {
          seat_id: seat.id,
          membership_id: who.id,
          kind,
          work_date: date,
          seconds: minutes * 60,
          note: get("note") || null,
        };
    } else {
      if (Number.isNaN(Date.parse(get("occurred_at"))))
        err("occurred_at", "invalid_timestamp", "occurred_at must be an ISO timestamp");
      if (
        !["de_usage", "agent_tokens", "agent_compute", "agent_tool", "adjustment"].includes(
          get("kind"),
        )
      )
        err("kind", "invalid", "unknown cost kind");
      if (!/^-?\d+(\.\d{1,2})?$/.test(get("amount")))
        err("amount", "invalid", "amount must be a number");
      if (!/^[A-Z]{3}$/.test(get("currency")))
        err("currency", "invalid", "currency is a 3-letter code");
      if (!get("meter_identity")) err("meter_identity", "required", "meter_identity is required");
      if (!["client", "agency"].includes(get("visibility")))
        err("visibility", "invalid", "visibility is client or agency");
    }
    return {
      result: {
        row: i + 1,
        status: errors.length ? "error" : "ok",
        ...(errors.length ? { errors } : {}),
      },
      ...(time ? { time } : {}),
    };
  });
  return { header: cols, rows };
}
